import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AppIcon } from './icon'
import { mdiCheck, mdiClose, mdiCloseCircleOutline } from '@mdi/js'
import { Button } from './button'
import { apiErrorDetail } from '../../lib/apiError'

type NullableDateFieldProps = {
    value: string | null | undefined
    onSave: (next: string | null) => Promise<void>
    disabled?: boolean
    testIdPrefix: string
    errorMessage?: string
    formatDisplay?: (iso: string) => string
}

const toInputValue = (value: string | null | undefined): string => {
    if (!value) return ''
    // Keep only YYYY-MM-DD for <input type="date">
    const match = /^(\d{4}-\d{2}-\d{2})/.exec(value)
    if (match) return match[1]
    const parsed = new Date(value)
    if (Number.isNaN(parsed.getTime())) return ''
    return parsed.toISOString().split('T')[0]
}

const toIsoDate = (raw: string): string | null => {
    const trimmed = raw.trim()
    if (!trimmed) return null
    const parsed = new Date(trimmed)
    if (Number.isNaN(parsed.getTime())) return trimmed
    return parsed.toISOString().split('T')[0]
}

export function NullableDateField({
    value,
    onSave,
    disabled = false,
    testIdPrefix,
    errorMessage,
    formatDisplay,
}: NullableDateFieldProps) {
    const { t } = useTranslation('common')
    const [editing, setEditing] = useState(false)
    const [draft, setDraft] = useState<string>(toInputValue(value))
    const [saving, setSaving] = useState(false)
    const [saveError, setSaveError] = useState<string | null>(null)
    // Escape / Cancel already settled the edit; the unmount blur must not save.
    const skipBlur = useRef(false)

    useEffect(() => {
        if (!editing) {
            setDraft(toInputValue(value))
        }
    }, [value, editing])

    const commit = async (next: string | null) => {
        setSaving(true)
        setSaveError(null)
        try {
            await onSave(next)
            setEditing(false)
        } catch (err) {
            // Keep the editor open on the refused value and say why.
            setSaveError(apiErrorDetail(err, t('dates.save_error')))
        } finally {
            setSaving(false)
        }
    }

    const startEditing = () => {
        skipBlur.current = false
        setSaveError(null)
        setEditing(true)
    }

    const handleSave = () => {
        void commit(toIsoDate(draft))
    }

    const handleClear = () => {
        setDraft('')
        void commit(null)
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault()
            handleSave()
        } else if (e.key === 'Escape') {
            e.preventDefault()
            skipBlur.current = true
            setDraft(toInputValue(value))
            setEditing(false)
        }
    }

    const disabledEditor = disabled || saving
    const shownError = errorMessage ?? saveError

    if (editing) {
        return (
            <div>
                <div
                    className="flex flex-wrap items-center gap-1 mt-1"
                    onBlur={(e) => {
                        // Focus left the editor (not just moved between its own
                        // buttons): blur saves, like the other click-to-edit rows.
                        if (e.currentTarget.contains(e.relatedTarget as Node | null)) return
                        if (skipBlur.current || disabledEditor) return
                        handleSave()
                    }}
                >
                    <input
                        type="date"
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={handleKeyDown}
                        onFocus={(e) => (e.currentTarget as HTMLInputElement & { showPicker?: () => void }).showPicker?.()}
                        className={`w-full px-2 py-1.5 border ${shownError ? 'border-destructive' : 'border-border'} rounded shadow-sm focus:outline-none focus:ring-ring focus:border-ring text-sm bg-background text-foreground`}
                        disabled={disabledEditor}
                        autoFocus
                        data-testid={`input-${testIdPrefix}`}
                    />
                    <Button
                        onClick={handleSave}
                        disabled={disabledEditor}
                        className="bg-primary hover:bg-primary/90 text-primary-foreground px-2 py-1 text-sm"
                        data-testid={`save-${testIdPrefix}`}
                        aria-label={t('actions.save')}
                    >
                        {saving ? '...' : <AppIcon path={mdiCheck} size="md" />}
                    </Button>
                    <Button
                        onClick={handleClear}
                        disabled={disabledEditor}
                        variant="outline"
                        className="px-2 py-1 text-sm"
                        data-testid={`clear-${testIdPrefix}`}
                        aria-label={t('dates.clear')}
                        title={t('dates.clear')}
                    >
                        <AppIcon path={mdiCloseCircleOutline} size="md" />
                    </Button>
                    <Button
                        onClick={() => {
                            skipBlur.current = true
                            setDraft(toInputValue(value))
                            setEditing(false)
                        }}
                        disabled={disabledEditor}
                        variant="outline"
                        className="px-2 py-1 text-sm"
                        data-testid={`cancel-${testIdPrefix}`}
                        aria-label={t('actions.cancel')}
                    >
                        <AppIcon path={mdiClose} size="md" />
                    </Button>
                </div>
                {shownError && (
                    <p className="mt-1 text-sm text-destructive" data-testid={`error-${testIdPrefix}`}>{shownError}</p>
                )}
            </div>
        )
    }

    const display = value
        ? (formatDisplay ? formatDisplay(value) : new Date(value).toLocaleDateString())
        : null

    return (
        <span
            className="-ml-2 text-sm text-foreground cursor-pointer hover:bg-muted/50 px-2 py-1.5 rounded transition-colors inline-block"
            onClick={() => !disabled && startEditing()}
            data-testid={`field-${testIdPrefix}`}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
                if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault()
                    startEditing()
                }
            }}
        >
            {display ?? <span className="text-muted-foreground">{t('dates.empty')}</span>}
        </span>
    )
}

export default NullableDateField
